import { getHttpClient } from '../lib/http-client';
import { CommentSuggestionsResponse } from '../schemas/bizy-comment-suggestions';
import { headers } from './meta';

enum QueryKey {
	CommentSuggestions = 'bizy-comment-suggestions',
}

export const bizyCommentSuggestionsPath = (prefix = '') => `${prefix}/bizy/comment_suggestions`;
export const bizyCommentSuggestionsRefinePath = (prefix = '') => `${bizyCommentSuggestionsPath(prefix)}/refine`;
export const bizyCommentSuggestionsAcceptPath = (prefix = '') => `${bizyCommentSuggestionsPath(prefix)}/accept`;

/** Draft stays out of the key so comment text is not retained in the RQ cache. */
export const bizyCommentSuggestionsQueryKey = (params: { bonusId: string; exclude: string[]; mode: 'ideas' | 'refine' }) =>
	[QueryKey.CommentSuggestions, params.mode, params.bonusId, params.exclude] as const;

export type CommentSuggestionsAPIData = CommentSuggestionsResponse;

type CommentSuggestionsParams = {
	bonusId: string;
	exclude?: string[];
	/** Round-trip so regenerate stays one Trends conversation. */
	conversationId?: string | null;
};

/**
 * Cold "Give me ideas" suggestions for the inline comment assist.
 *
 * GET /api/v2/bizy/comment_suggestions?bonus_id=…&exclude[]=…&conversation_id=…
 */
export const loadCommentSuggestions = async (params: CommentSuggestionsParams): Promise<CommentSuggestionsAPIData> => {
	const http = await getHttpClient();
	const exclude = (params.exclude ?? []).filter((text) => text.trim().length > 0);

	const res = await http.get(bizyCommentSuggestionsPath(), {
		headers,
		params: {
			bonus_id: params.bonusId,
			...(exclude.length > 0 ? { exclude } : {}),
			...(params.conversationId ? { conversation_id: params.conversationId } : {}),
		},
	});

	return CommentSuggestionsResponse.parse(res.data);
};

/**
 * Records that the user adopted a comment-assist suggestion so Bizy conversation
 * scoring can credit the session (action_taken / job_completion) instead of
 * scoring it as a prose-only run. Fire-and-forget: failures are swallowed so a
 * scoring-analytics blip never blocks posting the comment.
 *
 * POST /api/v2/bizy/comment_suggestions/accept { conversation_id, bonus_id, mode }
 */
export const recordCommentAssistAcceptance = async (params: { bonusId: string; conversationId: string; mode: 'ideas' | 'refine' }): Promise<void> => {
	try {
		const http = await getHttpClient();
		await http.post(bizyCommentSuggestionsAcceptPath(), { conversation_id: params.conversationId, bonus_id: params.bonusId, mode: params.mode }, { headers });
	} catch {
		// Non-critical analytics signal — never surface to the user.
	}
};

/**
 * Draft-aware "Make it shine" refine for the inline comment assist.
 *
 * POST /api/v2/bizy/comment_suggestions/refine { bonus_id, draft, exclude?, conversation_id? }
 */
export const refineCommentSuggestions = async (params: CommentSuggestionsParams & { draft: string }): Promise<CommentSuggestionsAPIData> => {
	const http = await getHttpClient();
	const exclude = (params.exclude ?? []).filter((text) => text.trim().length > 0);

	const res = await http.post(
		bizyCommentSuggestionsRefinePath(),
		{
			bonus_id: params.bonusId,
			draft: params.draft,
			...(exclude.length > 0 ? { exclude } : {}),
			...(params.conversationId ? { conversation_id: params.conversationId } : {}),
		},
		{ headers }
	);

	return CommentSuggestionsResponse.parse(res.data);
};
